import React from "react"
import { Box, Button, CircularProgress, Grid, Paper, Typography } from "@mui/material"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useAppDispatch } from "../../app/hooks"
import { setBookingDetails } from "../../features/PostCreat/postcreateSlice"
import {
  useAmountToPayMutation,
  useGetAcceptedGeneratedPostsAllQuery,
} from "../../features/OverViewAuth/overViewApi"

const PendingPayment = () => {
  const { bookingId } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { data, isLoading, isError } = useGetAcceptedGeneratedPostsAllQuery(
    { postId: bookingId },
    { skip: !bookingId }
  )
  const [amountToPay, { isLoading: isCalculating }] = useAmountToPayMutation()
  const booking = data?.data?.[0]

  const handlePayNow = async () => {
    if (!booking?._id) return
    if (booking.ispaymentVeified) {
      toast.info("Payment already completed")
      return
    }
    try {
      const paymentCalculations = await amountToPay({ bookingIds: [booking._id] }).unwrap()
      dispatch(setBookingDetails(booking))
      navigate("/dashboard/booking-overview", {
        state: {
          bookingIds: [booking._id],
          bookingDetails: booking,
          paymentCalculations: paymentCalculations?.data || paymentCalculations,
        },
      })
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to calculate")
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !booking) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
        <Typography variant="h5" fontWeight={700} color="text.secondary">Booking not found</Typography>
        <Typography variant="body2" color="text.secondary">This booking may have been removed or the link is invalid.</Typography>
        <Button variant="contained" onClick={() => navigate("/dashboard/my-bookings")} sx={{ backgroundColor: "#F475A4", "&:hover": { backgroundColor: "#D45E8C" } }}>
          Go to My Bookings
        </Button>
      </Box>
    )
  }

  const isPaid = booking.ispaymentVeified === true
  const serviceName = booking.service?.name || "Service"
  const formatDT = (v?: string) => v ? new Date(v).toLocaleString() : "—"

  return (
    <Box sx={{ px: { xs: 1, md: 3 }, py: 4 }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={7} lg={5}>
          <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#F475A4", mb: 0.5 }}>
              Pending Payment
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Complete payment to confirm your booking.
            </Typography>

            <Box sx={{ borderTop: "1px solid #eee", pt: 2 }}>
              {[
                { label: "Service", value: serviceName },
                { label: "Start", value: formatDT(booking.from) },
                { label: "End", value: formatDT(booking.till) },
                { label: "Status", value: isPaid ? "Paid" : "Payment Pending", color: isPaid ? "success.main" : "warning.main" },
              ].map((row) => (
                <Box key={row.label} display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: row.color || "text.primary", textAlign: "right" }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box display="flex" gap={2} mt={3} flexWrap="wrap">
              <Button
                variant="contained"
                disabled={isPaid || isCalculating}
                onClick={handlePayNow}
                sx={{
                  backgroundColor: "#F475A4",
                  "&:hover": { backgroundColor: "#D45E8C" },
                  textTransform: "none",
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                }}
              >
                {isCalculating ? "Calculating..." : isPaid ? "Paid" : "Pay Now"}
              </Button>
              <Button variant="outlined" onClick={() => navigate("/dashboard/my-bookings")} sx={{ textTransform: "none" }}>
                My Bookings
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PendingPayment
